import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useEffect, useRef, useState } from "react";
import InputMask from "react-input-mask";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";

import dayjs from "dayjs";
import AsyncSearch from "../../components/asyncSearch/AsyncSearch";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { Camera } from "../../components/camera/Camera";
import { ConfirmationArea } from "../../components/confirmationArea/ConfirmationArea";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { useAuth } from "../../hooks/auth";
import { ICollectorPlant } from "../../interfaces/ICollectorPlant";
import { IFormErrors } from "../../interfaces/IFormErrors";
import { IJudgment } from "../../interfaces/IJudgment";
import { IOption } from "../../interfaces/IOption";
import { IPageProps } from "../../interfaces/IPageProps";
import { IPlant } from "../../interfaces/IPlant";
import IPlantImage from "../../interfaces/IPlantImages";
import { IState } from "../../interfaces/IState";
import { IUser } from "../../interfaces/IUser";
import api from "../../services/api";
import getValidationError from "../../utils/getValidationError";

export const Plant: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const navigate = useNavigate();
  const location = useLocation();
  const toastr = useToastr();
  const { signOut } = useAuth();
  const timeout = useRef<any>(0);

  const [plantId, setPlantId] = useState<string>("");

  const [statesOptions, setStatesOptions] = useState<IOption[]>([]);
  const [stateOption, setStateOption] = useState<IOption | null>(null);

  const [number, setNumber] = useState("");
  const [plantNumber, setPlantNumber] = useState("");
  const [name, setName] = useState("");
  const [genealogy, setGenealogy] = useState("");
  const [collector, setCollector] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [address, setAddress] = useState("");
  const [addressNumber, setAddressNumber] = useState("");
  const [addressComplement, setAddressComplement] = useState("");
  const [addressReference, setAddressReference] = useState("");
  const [association, setAssociation] = useState("");
  const [imagePlants, setImagePlants] = useState<IPlantImage[] | null>(null);

  const [judgmentId] = useState<string>(() => {
    return new URLSearchParams(window.location.search).get("judgmentId") || "";
  });
  const [judgment, setJudgment] = useState<IJudgment | null>(null);

  const [dialogDeleteOpen, setDialogDeleteOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);

  const [formErrors, setFormErrors] = useState<IFormErrors>({});

  const [collectors, setCollectors] = useState<IUser[]>([]);
  const [collectorOptions, setCollectorOptions] = useState<IOption[]>([]);
  const [collectorOption, setCollectorOption] = useState<IOption | null>(null);
  const [loadingCollectors, setLoadingCollectors] = useState(false);

  const [collectorPlants, setCollectorPlants] = useState<ICollectorPlant[]>([]);
  const [collectorPlantsOptions, setCollectorPlantsOptions] = useState<
    IOption[]
  >([]);
  const [collectorPlantOption, setCollectorPlantOption] =
    useState<IOption | null>(null);
  const [loadingCollectorPlants, setLoadingCollectorPlants] = useState(false);

  const handleGoBack = useCallback(() => {
    navigate(
      `/plants?judgmentId=${judgmentId}&exhibitionId=${judgment?.exhibitionId}`,
    );
  }, [navigate, judgment?.exhibitionId, judgmentId]);

  const stateToOption = useCallback(
    (state: IState): IOption => ({
      value: `${state.id}`,
      description: `${state.id} - ${state.name}`,
    }),
    [],
  );

  const collectorToOption = (collector: IUser): IOption => {
    return {
      value: `${collector.id}`,
      description: `${collector.name}`,
    };
  };

  const collectorPlantToOption = (collectorPlant: ICollectorPlant): IOption => {
    return {
      value: `${collectorPlant.id}`,
      description: `${collectorPlant.number} - ${collectorPlant.name}`,
    };
  };

  const handleListCollectors = useCallback(
    async (filter = "") => {
      const exhibitionId = judgment?.exhibitionId;
      if (!exhibitionId) return;

      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoadingCollectors(true);

        await api
          .get(
            `/collectors/dynamically?perPage=100&currentPage=1&orderBy=name&orderDirection=asc&filter=${filter}&exhibitionId=${exhibitionId}`,
          )
          .then(result => {
            if (result) {
              setCollectors(result.data.data);
              const options: IOption[] =
                result.data.data.map(collectorToOption);

              setCollectorOptions(options);
            } else {
              setCollectorOptions([]);
            }
          })
          .catch(error => {
            setCollectorOptions([]);
            toastr.error(error.message);
          })
          .finally(() => {
            setLoadingCollectors(false);
          });
      }, timeout.current);
    },
    [judgment, toastr],
  );

  const handleListCollectorPlants = useCallback(
    async (filter = "") => {
      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        if (collectorOption?.value) {
          setLoadingCollectorPlants(true);

          await api
            .get(
              `/collector/plants/dynamically/exhibition/${judgment?.exhibitionId}/collector/${collectorOption?.value}?perPage=100&currentPage=1&orderBy=name&orderDirection=asc&filter=${filter}&onlyReceived=true`,
            )
            .then(result => {
              if (result) {
                setCollectorPlants(result.data.data);
                const options: IOption[] = result.data.data.map(
                  collectorPlantToOption,
                );

                setCollectorPlantsOptions(options);
              } else {
                setCollectorPlantsOptions([]);
              }
            })
            .catch(error => {
              setCollectorPlantsOptions([]);
              toastr.error(error.message);
            })
            .finally(() => {
              setLoadingCollectorPlants(false);
            });
        } else {
          toastr.warning("Nenhum colecionador selecionado.");
        }
      }, timeout.current);
    },
    [collectorOption, toastr, judgment?.exhibitionId],
  );

  const handleSetState = (value: string | null) => {
    if (value) {
      const selected = statesOptions.find(s => s.value === value);
      setStateOption(selected || null);
      setZipCode("");
      setCity("");
      setNeighborhood("");
      setAddress("");
      setAddressNumber("");
      setAddressComplement("");
      setAddressReference("");
    }
  };

  const handleSetCollectorData = useCallback(
    (option: IOption | null) => {
      const collector = collectors.find(cp => cp.id === option?.value);
      if (collector) {
        setCollector(collector.name || "");
        setZipCode(collector.zipCode || "");
        setCountry(collector.country || "");
        if (collector.state) {
          setStateOption(stateToOption(collector.state));
        }
        setCity(collector.city || "");
        setNeighborhood(collector.neighborhood || "");
        setAddress(collector.address || "");
        setAddressNumber(collector.addressNumber || "");
        setAddressComplement(collector.addressComplement || "");
        setAddressReference(collector.addressReference || "");
        setAssociation(collector.association || "");
      }
    },
    [collectors, stateToOption],
  );

  const handleSetCollector = (option: IOption | null) => {
    if (option) {
      setCollectorOption(option);
      const selected = collectorOptions.find(collectorOption => {
        return collectorOption.value === option.value;
      });
      if (selected?.value) {
        setCollectorOption(selected);
      } else {
        setCollectorOption(null);
      }

      handleSetCollectorData(option);
    }
  };

  const handleSetPlantData = useCallback(
    (option: IOption | null) => {
      const collectorPlant = collectorPlants.find(
        cp => cp.id === option?.value,
      );
      if (collectorPlant) {
        setNumber(collectorPlant.number ? String(collectorPlant.number) : "");
        setName(collectorPlant.name || "");
        setGenealogy(collectorPlant.genealogy || "");
      }
    },
    [collectorPlants],
  );

  const handleSetCollectorPlant = (option: IOption | null) => {
    if (option) {
      setCollectorPlantOption(option);
      const selected = collectorPlantsOptions.find(collectorPlantOption => {
        return collectorPlantOption.value === option.value;
      });
      if (selected?.value) {
        setCollectorPlantOption(selected);
      } else {
        setCollectorPlantOption(null);
      }

      handleSetPlantData(option);
    }
  };

  const handleListJudgment = useCallback(async () => {
    if (judgmentId) {
      setLoading(true);

      await api
        .get(`/judgments/${judgmentId}`)
        .then(response => {
          const data: IJudgment = response.data;
          setJudgment(data);
        })
        .catch(error => {
          if (error.status === 401) {
            signOut();
            navigate("/");
          }

          toastr.error(
            error.response.data?.message || "Contate a equipe de suporte",
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [navigate, signOut, toastr, judgmentId]);

  const handleListStates = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/states");
      const options = data.map(stateToOption);
      setStatesOptions(options);
    } catch (error: any) {
      if (error.status === 401) {
        signOut();
        navigate("/");
      }
      toastr.error(
        error.response?.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [signOut, toastr, navigate, stateToOption]);

  const handleSearchByNumber = useCallback(async () => {
    if (plantNumber && judgment?.exhibitionId) {
      setLoading(true);
      try {
        const { data } = await api.get(
          `/collector/plants/exhibition/${judgment.exhibitionId}/number/${plantNumber}`,
        );
        const collectorPlant: ICollectorPlant = data;

        if (collectorPlant) {
          setNumber(collectorPlant.number ? String(collectorPlant.number) : "");
          setName(collectorPlant.name || "");
          setGenealogy(collectorPlant.genealogy || "");

          if (collectorPlant.collector) {
            const collectorData = collectorPlant.collector;
            setCollector(collectorData.name || "");
            setZipCode(collectorData.zipCode || "");
            setCountry(collectorData.country || "");
            if (collectorData.state) {
              setStateOption(stateToOption(collectorData.state));
            }
            setCity(collectorData.city || "");
            setNeighborhood(collectorData.neighborhood || "");
            setAddress(collectorData.address || "");
            setAddressNumber(collectorData.addressNumber || "");
            setAddressComplement(collectorData.addressComplement || "");
            setAddressReference(collectorData.addressReference || "");
            setAssociation(collectorData.association || "");
          }
        }
      } catch (error: any) {
        if (error.response?.status === 401) {
          signOut();
          navigate("/");
        }
        toastr.error(
          error.response?.data?.message || "Planta não encontrada na exposição",
        );
      } finally {
        setLoading(false);
      }
    }
  }, [
    plantNumber,
    judgment?.exhibitionId,
    navigate,
    signOut,
    toastr,
    stateToOption,
  ]);

  const handleListPlant = useCallback(async () => {
    const id = location.pathname.replace("/plant", "").replace("/", "");
    if (id) {
      setPlantId(id);
      setLoading(true);
      try {
        const { data } = await api.get(`/plants/${id}`);
        const plant: IPlant = data;

        setNumber(plant.number || "");
        setName(plant.name || "");
        setGenealogy(plant.genealogy || "");
        setCollector(plant.collector || "");
        setZipCode(plant.zipCode || "");
        setCountry(plant.country || "");
        if (plant.state) {
          setStateOption(stateToOption(plant.state));
        }
        setCity(plant.city || "");
        setNeighborhood(plant.neighborhood || "");
        setAddress(plant.address || "");
        setAddressNumber(plant.addressNumber || "");
        setAddressComplement(plant.addressComplement || "");
        setAddressReference(plant.addressReference || "");
        setAssociation(plant.association || "");

        if (plant.collectorPlantId) {
          setCollectorPlantOption({
            value: plant.collectorPlantId,
            description: plant.collectorPlant?.name || "",
          });
        }

        if (!!plant.collectorPlant?.collector?.id) {
          setCollectorOption({
            value: plant.collectorPlant.collector.id,
            description: plant.collectorPlant.collector.name || "",
          });
        }

        setImagePlants(plant.plantImages || null);
      } catch (error: any) {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }
        toastr.error(
          error.response?.data?.message || "Contate a equipe de suporte",
        );
      } finally {
        setLoading(false);
      }
    }
  }, [location.pathname, navigate, signOut, toastr, stateToOption]);

  const handleRequestZipCode = useCallback(async () => {
    if (zipCode.length === 9) {
      setLoading(true);
      try {
        const { data } = await api.get(`/cep/${zipCode}`);
        setCountry("Brasil");
        setCity(data.localidade || "");
        setNeighborhood(data.bairro || "");
        setAddress(data.logradouro || "");
        setAddressNumber("");
        setAddressComplement("");
        setAddressReference("");
        const state = statesOptions.find(
          s => s.value.toUpperCase() === (data.uf || "").toUpperCase(),
        );
        setStateOption(state || null);
      } catch (error: any) {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }
        toastr.error(
          error.response?.data?.message || "Contate a equipe de suporte",
        );
      } finally {
        setLoading(false);
      }
    }
  }, [zipCode, statesOptions, navigate, signOut, toastr]);

  const handleDeletePlant = useCallback(async () => {
    if (plantId) {
      setLoading(true);
      try {
        await api.delete(`/plants/${plantId}`);
        navigate(
          `/plants?judgmentId=${judgmentId}&exhibitionId=${judgment?.exhibitionId}`,
        );
        toastr.success("Planta excluída com sucesso");
      } catch (error: any) {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }
        toastr.error(
          error.response?.data?.message || "Contate a equipe de suporte",
        );
      } finally {
        setLoading(false);
      }
    }
  }, [plantId, navigate, signOut, toastr, judgment?.exhibitionId, judgmentId]);

  const handleSubmit = useCallback(async () => {
    try {
      setFormErrors({});

      const judgmentId =
        new URLSearchParams(location.search).get("judgmentId") || "";

      if (!judgmentId) {
        toastr.error("Julgamento não pode ser vazio");
        return;
      }

      const schema = Yup.object().shape({
        judgmentId: Yup.string().required("Julgamento é obrigatório"),
        number: Yup.string().nullable().optional(),
        name: Yup.string().required("Nome é obrigatório"),
        genealogy: Yup.string().required("Genealogia é obrigatória"),
        collector: Yup.string().required("Coletor é obrigatório"),
      });

      const data: IPlant = {
        judgmentId,
        number,
        name,
        genealogy,
        collector,
        zipCode,
        country,
        stateId: stateOption?.value || "",
        city,
        neighborhood,
        address,
        addressNumber,
        addressComplement,
        addressReference,
        association,
        collectorPlantId: collectorPlantOption?.value,
      };

      await schema.validate(data, { abortEarly: false });

      setLoading(true);

      if (plantId) {
        await api.put(`/plants/${plantId}`, data);
        toastr.success("Planta atualizada com sucesso");
      } else {
        await api.post(`/plants`, data).then(response => {
          navigate(`/plant/${response.data.id}?judgmentId=${judgmentId}`);
        });
        toastr.success("Planta cadastrada com sucesso");
      }

      window.location.reload();
    } catch (error: any) {
      if (error instanceof Yup.ValidationError) {
        setFormErrors(getValidationError(error));
        return;
      }
      toastr.error(
        error.response?.data?.message || "Contate a equipe de suporte",
      );
    } finally {
      setLoading(false);
    }
  }, [
    plantId,
    number,
    name,
    genealogy,
    collector,
    zipCode,
    country,
    stateOption,
    city,
    neighborhood,
    address,
    addressNumber,
    addressComplement,
    addressReference,
    association,
    toastr,
    location,
    collectorPlantOption,
    navigate,
  ]);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageChange = useCallback(
    async (input: ChangeEvent<HTMLInputElement> | File) => {
      let file: File | null = null;

      // Permite receber tanto um evento quanto um File direto (para uso na captura da câmera)
      if (input instanceof File) {
        file = input;
      } else if (
        input &&
        input.target &&
        input.target.files &&
        input.target.files[0]
      ) {
        file = input.target.files[0];
      }

      if (!file) return;

      const data = new FormData();
      data.append("image", file);

      setLoading(true);
      await api
        .patch(`/plant-images/${plantId}`, data)
        .then(async () => {
          await handleListPlant();
          toastr.success("Imagem atualizada.");
        })
        .catch(error => {
          if (error.response?.status === 401) {
            signOut();
            navigate("/");
          }
          toastr.error(
            error?.response?.data?.message || "Erro ao alterar imagem.",
          );
        })
        .finally(() => {
          setLoading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        });
    },
    [plantId, navigate, toastr, signOut, handleListPlant],
  );

  const handleDeleteImage = useCallback(
    async (imageId: string | null) => {
      setLoading(true);
      try {
        await api.delete(`/plant-images/${imageId}`);
        await handleListPlant();
        toastr.success("Imagem excluída com sucesso.");
      } catch (error: any) {
        if (error.status === 401) {
          signOut();
          navigate("/");
        }
        toastr.error(
          error.response?.data?.message || "Contate a equipe de suporte",
        );
      } finally {
        setLoading(false);
      }
    },
    [navigate, signOut, toastr, handleListPlant],
  );

  useEffect(() => {
    Promise.all([handleListJudgment(), handleListPlant(), handleListStates()]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Camera
        open={cameraModalOpen}
        onClose={() => setCameraModalOpen(false)}
        onCapture={async file => {
          setCameraModalOpen(false); // FECHA O MODAL ANTES DE FAZER O UPLOAD
          await handleImageChange(file);
        }}
        handleImageChange={handleImageChange}
      />
      <TitleContainer
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        {judgment
          ? `Planta | ${judgment.exhibition?.name} ${dayjs(
              judgment.exhibition?.date,
            ).format("DD/MM/YYYY")} - ${judgment.category?.name} - ${
              judgment?.judgmentType?.name
            }`
          : "Planta"}
      </TitleContainer>
      <PageCard>
        <Typography
          variant="h6"
          color="primary"
          sx={{ fontWeight: 600, mb: 2 }}
        >
          Buscar plantas cadastradas na exposição
        </Typography>

        <Form>
          <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
            <Grid item xs={12} md={6} sm={6}>
              <InputMask
                mask={"99999999"}
                value={plantNumber}
                onChange={e => setPlantNumber(e.target.value)}
                maskPlaceholder={null}
                onBlur={handleSearchByNumber}
                autoFocus
              >
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="Número da Planta"
                />
              </InputMask>
            </Grid>

            <Grid item xs={12} md={6} sm={6}></Grid>

            <Grid item xs={12} md={6} sm={6}>
              <FormControl size="small" sx={{ width: "100%" }} required>
                <AsyncSearch
                  options={collectorOptions}
                  setOptions={setCollectorOptions}
                  option={collectorOption}
                  setOption={handleSetCollector}
                  asyncSearch={handleListCollectors}
                  loading={loadingCollectors}
                  label="Colecionador"
                  disabled={!!plantId}
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <FormControl size="small" sx={{ width: "100%" }} required>
                <AsyncSearch
                  options={collectorPlantsOptions}
                  setOptions={setCollectorPlantsOptions}
                  option={collectorPlantOption}
                  setOption={handleSetCollectorPlant}
                  asyncSearch={handleListCollectorPlants}
                  loading={loadingCollectorPlants}
                  label="Planta do Colecionador"
                  disabled={!collectorOption?.value}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Form>

        <Divider sx={{ width: "100%", marginY: 2 }} />

        <Typography
          variant="h6"
          color="primary"
          sx={{ fontWeight: 600, mb: 2 }}
        >
          Dados da planta
        </Typography>

        <Form>
          <Grid container spacing={2} sx={{ marginBottom: "20px" }}>
            <Grid item xs={12} md={3} sm={3}>
              <InputMask
                mask={"99999999"}
                value={number}
                onChange={e => setNumber(e.target.value)}
                maskPlaceholder={null}
                required
                disabled
              >
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="Número"
                  helperText={formErrors.number}
                  error={!!formErrors.number}
                  required
                />
              </InputMask>
            </Grid>

            <Grid item xs={12} md={9} sm={9}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Nome"
                value={name}
                required
                onChange={e => setName(e.target.value)}
                error={!!formErrors.name}
                helperText={formErrors.name}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Genealogia"
                value={genealogy}
                multiline
                rows={5}
                required
                onChange={e => setGenealogy(e.target.value)}
                error={!!formErrors.genealogy}
                helperText={formErrors.genealogy}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Colecionador"
                value={collector}
                required
                onChange={e => setCollector(e.target.value)}
                error={!!formErrors.collector}
                helperText={formErrors.collector}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <InputMask
                mask={"99999-999"}
                value={zipCode}
                onChange={e => setZipCode(e.target.value)}
                maskPlaceholder={null}
                onBlur={handleRequestZipCode}
              >
                <TextField
                  sx={{ width: "100%" }}
                  size="small"
                  label="CEP"
                  helperText={formErrors.zipCode}
                  error={!!formErrors.zipCode}
                />
              </InputMask>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="País"
                value={country}
                onChange={e => setCountry(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <FormControl fullWidth size="small" error={!!formErrors.stateId}>
                <InputLabel>Estado</InputLabel>
                <Select
                  label="Estado"
                  value={stateOption?.value || ""}
                  onChange={e => handleSetState(String(e.target.value))}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 48 * 5.5, // 5 itens visíveis (aprox. 48px cada)
                      },
                    },
                  }}
                >
                  {statesOptions.map(s => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.description}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>{formErrors.stateId}</FormHelperText>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Cidade"
                value={city}
                onChange={e => setCity(e.target.value)}
                error={!!formErrors.city}
                helperText={formErrors.city}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Bairro"
                value={neighborhood}
                onChange={e => setNeighborhood(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Endereço"
                value={address}
                onChange={e => setAddress(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Número"
                value={addressNumber}
                onChange={e => setAddressNumber(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Complemento"
                value={addressComplement}
                onChange={e => setAddressComplement(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Referência"
                value={addressReference}
                onChange={e => setAddressReference(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} md={6} sm={6}>
              <TextField
                sx={{ width: "100%" }}
                size="small"
                label="Associação"
                value={association}
                onChange={e => setAssociation(e.target.value)}
              />
            </Grid>
          </Grid>

          {imagePlants &&
            imagePlants?.length > 0 &&
            imagePlants.map((image, index) => (
              <Grid container spacing={2} key={`image-${index}`}>
                <Grid item xs={12} md={4} sm={6}>
                  <Box sx={{ position: "relative", width: "100%" }}>
                    {image.id && (
                      <>
                        <img
                          src={image.file}
                          alt={image.filename}
                          style={{
                            width: "100%",
                            height: "auto",
                            borderRadius: "8px",
                            display: "block",
                            zIndex: 1,
                            position: "relative",
                          }}
                        />
                        <IconButton
                          aria-label="Deletar imagem"
                          onClick={() => handleDeleteImage(image.id || null)}
                          sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            backgroundColor: "#fff",
                            "&:hover": { backgroundColor: "#f5f5f5" },
                            boxShadow: 1,
                            zIndex: 2,
                          }}
                        >
                          <DeleteIcon sx={{ color: "#d32f2f" }} />
                        </IconButton>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>
            ))}

          <ButtonGroup justformobilie>
            {plantId && (
              <ButtonTheme
                onClick={() => setCameraModalOpen(true)}
                variant="contained"
                color="warning"
                disabled={loading}
              >
                Tirar foto
              </ButtonTheme>
            )}

            <ButtonTheme
              onClick={handleGoBack}
              variant="contained"
              color="inherit"
              disabled={loading}
            >
              Voltar
            </ButtonTheme>

            {plantId && (
              <ButtonTheme
                onClick={() => setDialogDeleteOpen(true)}
                variant="contained"
                color="error"
                disabled={loading}
              >
                Excluir
              </ButtonTheme>
            )}

            <ButtonTheme
              onClick={handleSubmit}
              variant="contained"
              disabled={loading}
            >
              Confirmar
            </ButtonTheme>
          </ButtonGroup>
        </Form>

        <ConfirmationArea
          dialogOpen={dialogDeleteOpen}
          id={plantId}
          title="Deseja realmente excluir o item?"
          message=""
          key={`dialog-${plantId}`}
          handleConfirmation={handleDeletePlant}
          deny={() => setDialogDeleteOpen(false)}
        />

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
